import React from 'react';
import { Icon } from './Icon';
import { intl } from '../../lib';
import type { LanguageProperty } from '../generated/LanguageProperty';

type BadgeAriaProps =
  | { ariaDescription?: LanguageProperty; ariaDescriptionId?: string }
  | { ariaDescription: LanguageProperty; ariaDescriptionId: string };

export type BadgeProps = BadgeAriaProps & {
  className?: string;
  label?: string;
  badgeType: 'info' | 'error' | 'warning' | 'success';
};

/**
 * Badges indicate a status and/or should draw the attention of the user on a specific element.
 * If the use of the badge conveys any additional information, for example highlighting an unread document,
 * then either a label or an ariaDescription together with ariaDescriptionId are needed for screen readers.
 * If both are provided, label takes precedence.
 *
 * Hint: if you use ariaDescriptionId, don't forget to also add it to the element it is describing with the aria-describedby attribute.
 *
 * @param className additional className for the badge
 * @param label the optional text to display in the badge
 * @param badgeType the type of the badge, defining the color and icon
 * @param ariaDescription optional text to be read by screen readers
 * @param ariaDescriptionId id of the element containing ariaDescription
 * @param props leftover props
 */
export function Badge({
  className = '',
  label,
  badgeType,
  ariaDescription,
  ariaDescriptionId,
  ...props
}: Readonly<BadgeProps>): JSX.Element {
  const badgeClassName = ['ehs-badge', className, badgeType].join(' ');
  const icon = () => {
    switch (badgeType) {
      case 'error':
        return <Icon iconName="shui-cancel" aria-label="" />;
      case 'warning':
        return <Icon iconName="shui_warning" aria-label="" />;
      case 'success':
        return <Icon iconName="shui_check" aria-label="" />;
      default:
        return <></>;
    }
  };

  return (
    <span {...props} className={badgeClassName}>
      {icon()}
      {label ? (
        <span>{label}</span>
      ) : (
        ariaDescription && (
          <div id={ariaDescriptionId} className="sr-only">
            {intl(ariaDescription)}
          </div>
        )
      )}
    </span>
  );
}
